/**
 * DeckCardRow - Grid-based card row with configurable columns
 *
 * Uses display: contents to participate in parent CSS Grid. Each cell is a direct
 * grid child, allowing columns to align across all sections and rows.
 *
 * Fixed columns: qty, img, name, actions
 * Optional columns: supertype, type, subtype, fullType, manaCost, cmc, colorIdentity,
 *   manaProduced, rarity, powerToughness, power, toughness, price, set, setNumber,
 *   collectorNumber, released, roles, keywords
 */
import type { ReactElement } from 'react';
import { useCallback } from 'react';

import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { FaCrown } from 'react-icons/fa';
import { IoCheckbox, IoSquareOutline } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import CardImage from '@/components/CardImage/CardImage';
import ColorDots from '@/components/ColorDots/ColorDots';
import KeywordIcons from '@/components/KeywordIcons/KeywordIcons';
import SplitManaCost from '@/components/ManaSymbol/SplitManaCost';
import ProducedMana from '@/components/ProducedMana/ProducedMana';
import QuantityStepper from '@/components/QuantityStepper/QuantityStepper';
import RarityBadge from '@/components/RarityBadge/RarityBadge';
import RoleTags from '@/components/RoleTags/RoleTags';
import { deckEditorSettingsAtom } from '@/features/decks/state/deckEditorSettings';
import useUserSettings from '@/features/settings/hooks/api';
import { useDeckCardOwnership } from '@/hooks/useCollectionAvailability';
import { formatPowerToughnessFull, getDisplayManaCost, getFullTypeLine, getPower, getToughness } from '@/lib/cardUtils';
import { formatPrice, getSimplePriceFromPrices } from '@/lib/price';
import type { CardPrices, Color } from '@/types/card';
import type { DeckFormat, DeckStatus } from '@/types/deck';
import useDeckCardOperations from '../hooks/useDeckCardOperations';
import { EnrichedDeckCard } from '../types';
import DeckCardActions from './DeckCardActions';

// ============================================================================
// Subcomponents
// ============================================================================

interface CmcBadgeProps {
  cmc: number;
}

function CmcBadge({ cmc }: CmcBadgeProps): ReactElement {
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-700/80 text-xs font-semibold text-zinc-200">{cmc}</span>
  );
}

import type { DeckCardOwnership } from '@/hooks/useCollectionAvailability';

interface OwnershipIndicatorProps {
  ownership: DeckCardOwnership;
}

/**
 * Displays ownership status for a deck card.
 * Emphasis is inverted relative to severity: "not owned" is the state the user
 * has to act on, so it is the loudest; "owned" needs no attention and recedes.
 * - Red, emphasized: not owned
 * - Amber: fewer copies owned than the deck needs
 * - Dim green + amber suffix: owns enough, but copies are committed to other active decks
 * - Dim green: fully covered
 * Also indicates when using different printings.
 */
function OwnershipIndicator({ ownership }: OwnershipIndicatorProps): ReactElement {
  const {
    covered,
    needed,
    totalOwnedByName,
    exactCovered,
    otherVersionsCovered,
    isFullyCovered,
    hasAny,
    committedElsewhere,
    plannedElsewhere,
    freeForThisDeck,
    allocationShortfall,
  } = ownership;

  // Build tooltip with detailed breakdown
  const tooltipParts: string[] = [];
  if (!hasAny) {
    tooltipParts.push('Not in collection');
  } else {
    tooltipParts.push(`${totalOwnedByName} owned`);
    if (exactCovered > 0) tooltipParts.push(`${exactCovered} exact`);
    if (otherVersionsCovered > 0) tooltipParts.push(`${otherVersionsCovered} other version${otherVersionsCovered > 1 ? 's' : ''}`);
    if (committedElsewhere > 0) tooltipParts.push(`${committedElsewhere} in other active decks`);
    if (plannedElsewhere > 0) tooltipParts.push(`${plannedElsewhere} planned in other brews`);
    tooltipParts.push(`${Math.max(0, freeForThisDeck)} free`);
  }
  const tooltip = tooltipParts.join(' · ');

  // Not owned at all
  if (!hasAny) {
    return (
      <span className="text-danger ml-2 font-semibold" title={tooltip}>
        Not owned
      </span>
    );
  }

  const otherVersionsNote = otherVersionsCovered > 0 ? <span className="ml-1 text-zinc-500">({otherVersionsCovered} other)</span> : null;

  // Owns fewer copies than the deck needs
  if (!isFullyCovered) {
    return (
      <span className="ml-2" title={tooltip}>
        <span className="font-medium text-amber-400">
          {covered}/{needed}
        </span>
        {otherVersionsNote}
      </span>
    );
  }

  return (
    <span className="ml-2" title={tooltip}>
      <span className="text-emerald-400/50">Owned</span>
      {allocationShortfall > 0 && <span className="ml-1 font-medium text-amber-400">· {allocationShortfall} short</span>}
      {otherVersionsNote}
    </span>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export interface DeckListViewRowProps {
  deckCard: EnrichedDeckCard;
  deckId: string;
  deckFormat: DeckFormat | null;
  deckStatus: DeckStatus;
  isCover: boolean;
  isLocked?: boolean;
  /** Render as commander row with amber accent styling */
  isCommander?: boolean;
  /** Render as companion row with purple accent styling */
  isCompanion?: boolean;
  onSetCover: (scryfallId: string) => void;
  onSetCommander?: (cardId: string, scryfallId: string) => void;
  /** Called when card name/image is clicked to open details panel */
  onCardSelect?: (scryfallId: string, deckCardId: string) => void;
  /** Whether multi-select mode is active */
  isSelectionMode?: boolean;
  /** Whether this row is currently selected */
  isSelected?: boolean;
  /** Toggle selection for this deck card */
  onToggleSelect?: (deckCardId: string) => void;
}

export default function DeckListViewRow({
  deckCard,
  deckId,
  deckFormat,
  deckStatus,
  isCover,
  isLocked = false,
  isCommander = false,
  isCompanion = false,
  onSetCover,
  onSetCommander,
  onCardSelect,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelect,
}: DeckListViewRowProps): ReactElement {
  const { increase, decrease, canIncrease, isLoading: isUpdating } = useDeckCardOperations({ deckId, isLocked, deckFormat });
  const editorSettings = useAtomValue(deckEditorSettingsAtom);
  const columnVisibility = editorSettings.columnVisibility;
  const { settings: userSettings } = useUserSettings();
  const currency = userSettings.currency;

  const { card, quantity, id: cardId, scryfall_id, zone: currentZone } = deckCard;
  const canModify = !isLocked && !isUpdating;
  const canAdd = canModify && card && canIncrease(card, quantity);

  // Get dynamic ownership info from collection, excluding this deck's own copies
  const ownership = useDeckCardOwnership(scryfall_id, card?.name_lower ?? null, quantity, deckId);

  // Get card gameplay roles (ramp, draw, removal, etc.) from pre-computed classification
  const activeRoles = deckCard.classification.active_roles;

  // Handle card selection (opens details panel, or toggles multi-select)
  const handleCardSelect = useCallback(() => {
    if (isSelectionMode) {
      onToggleSelect?.(cardId);
      return;
    }
    if (onCardSelect) {
      onCardSelect(scryfall_id, cardId);
    }
  }, [isSelectionMode, onToggleSelect, onCardSelect, scryfall_id, cardId]);

  // Loading state - use display: contents with skeleton cells
  if (!card) {
    return (
      <div className="contents">
        {/* Qty cell skeleton */}
        <div className="flex items-center justify-center py-2">
          <div className="h-5 w-8 animate-pulse rounded bg-zinc-700" />
        </div>
        {/* Image cell skeleton */}
        <div className="flex items-center justify-center py-2">
          <div className="h-14 w-10 animate-pulse rounded bg-zinc-700" />
        </div>
        {/* Name cell skeleton */}
        <div className="flex items-center py-2">
          <div className="h-4 w-32 animate-pulse rounded bg-zinc-700" />
        </div>
        {/* Fill remaining columns with empty placeholders */}
        {Object.entries(columnVisibility)
          .filter(([, visible]) => visible)
          .map(([key]) => (
            <div key={key} className="flex items-center py-2" />
          ))}
        {/* Actions cell skeleton */}
        <div className="flex items-center py-2" />
      </div>
    );
  }

  const imageUris = deckCard.display_image_uris;
  const displayTypes = deckCard.classification.main_type;
  const subtypes = deckCard.classification.sub_type || null;
  const fullTypeLine = getFullTypeLine(card);
  const manaCost = getDisplayManaCost(card);
  const power = getPower(card);
  const toughness = getToughness(card);

  // Determine row accent styling based on commander/companion status
  const rowAccentClass = isCommander
    ? 'border-l-4 border-amber-500 bg-amber-500/5'
    : isCompanion
      ? 'border-l-4 border-purple-500 bg-purple-500/5'
      : '';

  // Cell base styling
  const cellBase = 'py-2 px-1 flex items-center';

  return (
    <div
      className={clsx(
        'group contents',
        isUpdating && 'opacity-60',
        // These classes are applied via CSS to all direct children for hover effect
      )}
      data-row-accent={rowAccentClass}
      data-selected={isSelectionMode && isSelected ? 'true' : undefined}
    >
      {/* ===== FIXED: Quantity Cell ===== */}
      <div className={clsx(cellBase, 'justify-center', rowAccentClass, 'rounded-l-lg transition-colors group-hover:bg-zinc-800/50')}>
        {isSelectionMode ? (
          <button
            type="button"
            onClick={() => onToggleSelect?.(cardId)}
            className="focus-visible:ring-aether flex items-center justify-center rounded focus:outline-none focus-visible:ring-2"
            aria-label={isSelected ? `Deselect ${card.name}` : `Select ${card.name}`}
            aria-pressed={isSelected}
          >
            {isSelected ? (
              <IoCheckbox className="text-aether h-5 w-5" />
            ) : (
              <IoSquareOutline className="text-ash hover:text-pearl h-5 w-5" />
            )}
          </button>
        ) : isLocked ? (
          <span className="text-sm font-medium text-zinc-400">{quantity}×</span>
        ) : (
          <QuantityStepper
            orientation="vertical"
            value={quantity}
            onIncrease={() => increase(cardId, quantity)}
            onDecrease={() => decrease(cardId, quantity)}
            canIncrease={canAdd}
            canDecrease={canModify}
            increaseLabel={`Increase quantity of ${card.name}`}
            decreaseLabel={`Decrease quantity of ${card.name}`}
          />
        )}
      </div>

      {/* ===== FIXED: Image Cell ===== */}
      <div
        className={clsx(
          cellBase,
          'justify-center',
          !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
          isCommander && 'bg-amber-500/5',
          isCompanion && 'bg-purple-500/5',
        )}
      >
        <CardHoverPreview imageUris={imageUris} name={card.name} layout={card.layout} cardFaces={card.card_faces}>
          <button
            type="button"
            onClick={handleCardSelect}
            className="focus-visible:ring-aether shrink-0 rounded focus:outline-none focus-visible:ring-2"
            aria-label={`View details for ${card.name}`}
          >
            <CardImage imageUris={imageUris} name={card.name} size="small" className="h-14! w-10!" />
          </button>
        </CardHoverPreview>
      </div>

      {/* ===== FIXED: Name Cell ===== */}
      <div
        className={clsx(
          cellBase,
          'min-w-0 flex-col items-start justify-center',
          !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
          isCommander && 'bg-amber-500/5',
          isCompanion && 'bg-purple-500/5',
        )}
      >
        <div className="flex w-full items-center gap-2">
          {/* Commander indicator */}
          {(currentZone === 'commander' || isCommander) && <FaCrown className="h-3.5 w-3.5 shrink-0 text-amber-400" title="Commander" />}
          <button
            type="button"
            onClick={handleCardSelect}
            className="truncate font-medium text-zinc-100 hover:text-zinc-50 hover:underline focus:outline-none focus-visible:underline"
          >
            {card.name}
          </button>
        </div>
        <p className="w-full truncate text-xs text-zinc-500">
          {card.set.toUpperCase()} #{card.collector_number}
          <OwnershipIndicator ownership={ownership} />
        </p>
      </div>

      {/* ===== DYNAMIC COLUMNS ===== */}

      {/* Supertype column */}
      {columnVisibility.supertype && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-end overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          {deckCard.classification.super_type ? (
            <span className="truncate text-xs text-zinc-400">{deckCard.classification.super_type}</span>
          ) : null}
        </div>
      )}

      {/* Type column */}
      {columnVisibility.type && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-end overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <span className="truncate text-xs text-zinc-400" title={fullTypeLine ?? undefined}>
            {displayTypes}
          </span>
        </div>
      )}

      {/* Subtype column */}
      {columnVisibility.subtype && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-end overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          {subtypes ? (
            <span className="truncate text-xs text-zinc-400" title={subtypes}>
              {subtypes}
            </span>
          ) : null}
        </div>
      )}

      {/* Full Type column */}
      {columnVisibility.fullType && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-end overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <span className="truncate text-xs text-zinc-400" title={fullTypeLine ?? undefined}>
            {fullTypeLine}
          </span>
        </div>
      )}

      {/* Roles column */}
      {columnVisibility.roles && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-start overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          {activeRoles.length > 0 ? (
            <RoleTags roles={activeRoles} maxVisible={2} iconOnly className="gap-0.5" />
          ) : (
            <span className="text-xs text-zinc-600">—</span>
          )}
        </div>
      )}

      {/* Keywords column — standalone grid cell with mana-font ability icons */}
      {columnVisibility.keywords && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-start overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <KeywordIcons keywords={card.keywords} maxVisible={3} />
        </div>
      )}

      {/* Mana Cost column (symbols) */}
      {columnVisibility.manaCost && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-end overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          {manaCost ? <SplitManaCost cost={manaCost} shadow={false} iconSize={20} /> : <span className="text-xs text-zinc-600">—</span>}
        </div>
      )}

      {/* CMC column */}
      {columnVisibility.cmc && (
        <div
          className={clsx(
            cellBase,
            'justify-center overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <CmcBadge cmc={card.cmc ?? 0} />
        </div>
      )}

      {/* Color Identity column */}
      {columnVisibility.colorIdentity && (
        <div
          className={clsx(
            cellBase,
            'justify-center overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <ColorDots colors={card.color_identity} />
        </div>
      )}

      {/* Mana Produced column */}
      {columnVisibility.manaProduced && (
        <div
          className={clsx(
            cellBase,
            'justify-center overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <ProducedMana colors={(card as { produced_mana?: Color[] | null }).produced_mana ?? null} iconSize={20} />
        </div>
      )}

      {/* Rarity column */}
      {columnVisibility.rarity && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-center overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <RarityBadge rarity={card.rarity} variant="icon" className="text-[20px]!" />
        </div>
      )}

      {/* Power/Toughness column */}
      {columnVisibility.powerToughness && (
        <div
          className={clsx(
            cellBase,
            'justify-center overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <span className="text-xs font-medium text-zinc-300">{formatPowerToughnessFull(card)}</span>
        </div>
      )}

      {/* Power column */}
      {columnVisibility.power && (
        <div
          className={clsx(
            cellBase,
            'justify-center overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          {power != null ? <span className="text-xs font-medium text-zinc-300">{power}</span> : null}
        </div>
      )}

      {/* Toughness column */}
      {columnVisibility.toughness && (
        <div
          className={clsx(
            cellBase,
            'justify-center overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          {toughness != null ? <span className="text-xs font-medium text-zinc-300">{toughness}</span> : null}
        </div>
      )}

      {/* Price column */}
      {columnVisibility.price && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-end overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <span className="truncate text-xs text-zinc-400">
            {(() => {
              const price = getSimplePriceFromPrices((card as { prices?: CardPrices }).prices, currency);
              return price !== null ? formatPrice(price, currency) : '—';
            })()}
          </span>
        </div>
      )}

      {/* Set column */}
      {columnVisibility.set && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-end overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <span className="truncate text-xs text-zinc-400" title={(card as { set_name?: string }).set_name ?? card.set}>
            {(card as { set_name?: string }).set_name ?? card.set.toUpperCase()}
          </span>
        </div>
      )}

      {/* Set # column (SET + collector number) */}
      {columnVisibility.setNumber && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-end overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <span className="truncate text-xs text-zinc-400">
            {card.set.toUpperCase()} #{card.collector_number}
          </span>
        </div>
      )}

      {/* Collector # column */}
      {columnVisibility.collectorNumber && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-end overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <span className="truncate text-xs text-zinc-400">#{card.collector_number}</span>
        </div>
      )}

      {/* Released column */}
      {columnVisibility.released && (
        <div
          className={clsx(
            cellBase,
            'min-w-0 justify-end overflow-hidden',
            !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
            isCommander && 'bg-amber-500/5',
            isCompanion && 'bg-purple-500/5',
          )}
        >
          <span className="truncate text-xs text-zinc-400">{(card as { released_at?: string }).released_at ?? ''}</span>
        </div>
      )}

      {/* ===== FIXED: Actions Cell ===== */}
      <div
        className={clsx(
          cellBase,
          'justify-end gap-1 rounded-r-lg',
          !isCommander && !isCompanion && 'transition-colors group-hover:bg-zinc-800/50',
          isCommander && 'bg-amber-500/5',
          isCompanion && 'bg-purple-500/5',
        )}
      >
        {!isSelectionMode && (
          <DeckCardActions
            deckCard={deckCard}
            deckId={deckId}
            deckFormat={deckFormat}
            deckStatus={deckStatus}
            isCover={isCover}
            isLocked={isLocked}
            variant="row"
            onSetCover={onSetCover}
            onSetCommander={isCommander ? undefined : onSetCommander}
          />
        )}
      </div>
    </div>
  );
}
